import React, { useState, useEffect, useRef } from 'react';
import { useAbility } from '@casl/react';
import { useDispatch, useSelector } from 'react-redux';
import { Col, Row, Spinner } from 'react-bootstrap';
import 'react-circular-progressbar/dist/styles.css';
import camelCaseRecursive from 'camelcase-keys-recursive';
import { AbilityContext } from '../../../config/abilityContext';
import { debounceIndexVacationsRequest } from '../../../requests/vacations';
import { debounceIndexPermissionsRequest } from '../../../requests/permissions';
import { debounceIndexLoanRequest } from '../../../requests/loans';
import { debounceIndexShiftChangeRequest } from '../../../requests/shiftChange';
import { debounceIndexAdvancePaymentRequest } from '../../../requests/advancePayments';
import { profilePermissions } from '../../../requests/dashboards';
import { debounceIndexOvertimeRequest } from '../../../requests/overtimes';
import { debounceIndexOtherRequestRequest } from '../../../requests/otherRequests';
import LinkBtnDashboard from './LinkBtnDashboard';

const MyRequests = ({ currentEmployee, setTotalItemsRequest }) => {
  const ability = useAbility(AbilityContext);
  const { advanceSettings } = useSelector(state => state.utils);
  const dispatch = useDispatch();
  const requestsAbility = [
    ability.can('index', 'AdvancePayment'),
    ability.can('index', 'Permission'),
    ability.can('index', 'Vacation'),
    ability.can('index', 'Loan'),
    ability.can('index', 'ShiftChangeRequest'),
    ability.can('index', 'Overtime'),
    ability.can('index', 'OvertimeCompensation'),
    ability.can('index', 'OtherRequest')
  ];

  const [vacations, setVacations] = useState([]);
  const [administrative, setAdministrative] = useState(0);
  const [permissions, setPermissions] = useState([]);
  const [loans, setLoans] = useState([]);
  const [shiftChange, setShiftChange] = useState([]);
  const [advancePayments, setAdvancePayments] = useState([]);
  const [overtimeRequest, setOvertimeRequest] = useState([]);
  const [otherRequests, setOtherRequests] = useState([]);
  const [showSpinner, setShowSpinner] = useState(1);
  const isMounted = useRef(true);

  const vacationsRole = advanceSettings
    .filter(asItem => ['legal_holiday', 'progressive_holiday', 'additional_days'].includes(asItem.code))
    .some(x => x.textValue === 'true');

  const loanRole = advanceSettings.filter(asItem => asItem.code === 'loan_request').some(x => x.textValue === 'true');

  const permissionsRole = advanceSettings
    .filter(asItem => ['paid_leave', 'without_paid_leave', 'administrative_days'].includes(asItem.code))
    .some(x => x.textValue === 'true');

  const shiftChangeRole = advanceSettings
    .filter(asItem => asItem.code === 'shift_change')
    .some(x => x.textValue === 'true');

  const advancePaymentsRole = advanceSettings
    .filter(asItem => asItem.code === 'advance_payment_request')
    .some(x => x.textValue === 'true');

  const administrativeDaysRole = advanceSettings
    .filter(asItem => ['administrative_days'].includes(asItem.code))
    .some(x => x.textValue === 'true');

  const overtimeRequestRole = advanceSettings
    .filter(asItem => asItem.code === 'overtime_requests')
    .some(x => x.textValue === 'true');

  const overtimeCompensationVacationRole = advanceSettings
    .filter(asItem => asItem.code === 'overtime_compensation_vacation_request')
    .some(x => x.textValue === 'true');

  const otherRequestsRole = advanceSettings
    .filter(asItem => asItem.code === 'toggle_other_requests')
    .some(x => x.textValue === 'true');

  const cardRequest = advanceSettings
    .filter(asItem =>
      [
        'legal_holiday',
        'progressive_holiday',
        'additional_days',
        'paid_leave',
        'without_paid_leave',
        'administrative_days',
        'shift_change',
        'loan_request',
        'advance_payment_request',
        'overtime_requests'
      ].includes(asItem.code)
    )
    .some(x => x.textValue === 'true');

  useEffect(() => {
    const calculateTotalItemsRequest = () => {
      let total = 0;
      if (vacationsRole && requestsAbility[2]) total += 1;
      if (permissionsRole && requestsAbility[1]) total += 1;
      if (loanRole && requestsAbility[3]) total += 1;
      if (advancePaymentsRole && requestsAbility[0]) total += 1;
      if (shiftChangeRole && requestsAbility[4]) total += 1;
      if (overtimeRequestRole && requestsAbility[5]) total += 1;
      if (overtimeCompensationVacationRole) total += 1;
      if (administrativeDaysRole && requestsAbility[6]) total += 1;
      if (otherRequestsRole && requestsAbility[7]) total += 1;
      return total;
    };

    if (advanceSettings.length > 0) {
      setTotalItemsRequest(calculateTotalItemsRequest());
    }
  }, [
    advanceSettings,
    setTotalItemsRequest,
    vacationsRole,
    requestsAbility,
    permissionsRole,
    loanRole,
    advancePaymentsRole,
    shiftChangeRole,
    overtimeRequestRole,
    administrativeDaysRole,
    overtimeCompensationVacationRole,
    otherRequestsRole
  ]);

  const handleIndexRequest = () => {
    const sendParams = { filter_employee: currentEmployee, status: 'pending' };
    if (currentEmployee) {
      if (vacationsRole) {
        debounceIndexVacationsRequest({
          dispatch,
          params: sendParams,
          successCallback: response => {
            if (isMounted.current) {
              const { data } = response.data;
              const countVacations = data.filter(asItem => ['legal', 'additional_days'].includes(asItem.vacation_type));
              const countAdministrative = data.filter(asItem => asItem.vacation_type === 'administrative_days');
              setVacations(countVacations.length);
              setAdministrative(countAdministrative.length);
              setShowSpinner(0);
            }
          }
        });
      }

      profilePermissions({
        dispatch,
        successCallback: response => {
          setAdministrative(camelCaseRecursive(response.data.administrative_vacation_card));
        }
      });

      debounceIndexPermissionsRequest({
        dispatch,
        params: sendParams,
        successCallback: response => {
          const { data } = response.data;
          setPermissions(data.length);
          setShowSpinner(0);
        }
      });

      debounceIndexLoanRequest({
        dispatch,
        params: sendParams,
        successCallback: response => {
          const { data } = response.data;
          setLoans(data.length);
          setShowSpinner(0);
        }
      });

      if (shiftChangeRole) {
        debounceIndexShiftChangeRequest({
          dispatch,
          params: sendParams,
          successCallback: response => {
            const { data } = response.data;
            setShiftChange(data.length);
            setShowSpinner(0);
          },
          failureCallback: () => null
        });
      }

      debounceIndexAdvancePaymentRequest({
        dispatch,
        params: sendParams,
        successCallback: response => {
          const { data } = response.data;
          setAdvancePayments(data.length);
          setShowSpinner(0);
        }
      });

      if (overtimeRequestRole) {
        debounceIndexOvertimeRequest({
          dispatch,
          params: sendParams,
          successCallback: response => {
            const { data } = response.data;
            setOvertimeRequest(data.length);
            setShowSpinner(0);
          }
        });
      }

      if (otherRequestsRole) {
        debounceIndexOtherRequestRequest({
          dispatch,
          params: sendParams,
          successCallback: response => {
            const { data } = response.data;
            setOtherRequests(data.length);
            setShowSpinner(0);
          }
        });
      }
    }
  };
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);
  useEffect(handleIndexRequest, [currentEmployee]);

  return (
    <>
      <div
        id="HRDashboard"
        style={{
          backgroundColor: '#fff',
          border: '1px solid #E0E0E0',
          borderRadius: '8px',
          height: '100%'
        }}
      >
        <div
          style={{
            textAlign: 'center',
            backgroundColor: '#fff',
            borderTopRightRadius: 8,
            borderTopLeftRadius: 8
          }}
        >
          <Row>
            <Col className="mt-2" md={8} xl={8} xs={8}>
              <p
                style={{
                  fontSize: '12px',
                  fontWeight: 'bold',
                  color: '#827f7f',
                  textAlign: 'left',
                  marginLeft: '10px'
                }}
              >
                Gestiona Tus Solicitudes
              </p>
            </Col>
          </Row>
        </div>

        <div style={{ paddingRight: 15, paddingLeft: 15 }}>
          <Row className="align-items-center">
            {showSpinner ? (
              <Col className="div-content mt-5 mb-5">
                <div className="containerSpinnerLoadDash" style={{ position: 'absolute', height: '100%' }}>
                  <Spinner animation="border" variant="primary" />
                </div>
              </Col>
            ) : (
              <>
                <LinkBtnDashboard
                  role={vacationsRole && requestsAbility[2]}
                  to="/profile/requests?tab=vacations"
                  icon="sunny"
                  text={`Vacaciones${vacations > 0 ? ` (${vacations})` : ''}`}
                />

                <LinkBtnDashboard
                  role={permissionsRole && requestsAbility[1]}
                  to="/profile/requests?tab=permissions"
                  icon="user-check"
                  text={`Permisos${permissions > 0 ? ` (${permissions})` : ''}`}
                />

                <LinkBtnDashboard
                  role={administrativeDaysRole && permissionsRole && requestsAbility[1]}
                  to="/profile/requests?tab=administrative_days"
                  icon="calendar"
                  text={`Días Admin.${administrative > 0 ? ` (${administrative})` : ''}`}
                />

                <LinkBtnDashboard
                  role={overtimeCompensationVacationRole && requestsAbility[6]}
                  to="/profile/requests?tab=overtime_compensations"
                  icon="calendar"
                  text="Días por HH.EE"
                />

                <LinkBtnDashboard
                  role={advancePaymentsRole && requestsAbility[0]}
                  to="/profile/requests?tab=advance_payments"
                  icon="wallet"
                  text={`Anticipos${advancePayments > 0 ? ` (${advancePayments})` : ''}`}
                />

                <LinkBtnDashboard
                  role={loanRole && requestsAbility[3]}
                  to="/profile/requests?tab=loans"
                  icon="wallet"
                  text={`Préstamos${loans > 0 ? ` (${loans})` : ''}`}
                />

                <LinkBtnDashboard
                  role={shiftChangeRole && requestsAbility[4]}
                  to="/profile/requests?tab=shift_change"
                  icon="calendar"
                  text={`Cambio Turno${shiftChange > 0 ? ` (${shiftChange})` : ''}`}
                />

                <LinkBtnDashboard
                  role={overtimeRequestRole && requestsAbility[5]}
                  to="/profile/requests?tab=overtimes"
                  icon="clock2"
                  text={`Horas Extras${overtimeRequest > 0 ? ` (${overtimeRequest})` : ''}`}
                />

                <LinkBtnDashboard
                  role={otherRequestsRole && requestsAbility[7]}
                  to="/profile/requests?tab=other_requests"
                  icon="user-check"
                  text={`Otras Solicitudes${otherRequests > 0 ? ` (${otherRequests})` : ''}`}
                />
              </>
            )}
          </Row>
          {!cardRequest && !showSpinner && (
            <Row>
              <Col>
                <p className="mt-2" style={{ height: 118, textAlign: 'center' }}>
                  No existen solicitudes asociadas...
                </p>
              </Col>
            </Row>
          )}
        </div>
      </div>
    </>
  );
};

export default MyRequests;
